import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  orgCalendar,
  DAY_TYPE_LABELS,
  type CalendarDay,
  type DayType,
} from '@/api/production_calendar';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import Drawer from '@/components/Drawer';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useOrgFilter } from '@/store/defaults';
import { withCode } from '@/shared/utils/labels';

/**
 * Виробничий календар ОРГАНІЗАЦІЇ (ч.1118).
 *
 * Календар індивідуальний: типовий календар обраного режиму (воєнний стан /
 * мирний час) + власні дні організації. Від нього залежать норма робочих днів
 * для зарплати, автозаповнення табеля, фонд оплати у штатному розкладі,
 * тривалість і оплата відпустки, розрахунковий період відпускних.
 *
 * Перегляд — усім, хто працює з кадрами й зарплатою; зміна — окреме право
 * `refs.production_calendar.edit` (перевіряється на сервері).
 */
const MONTHS = [
  'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень',
];
const DOW = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];
const TYPE_ORDER: DayType[] = ['WORKDAY', 'WEEKEND', 'HOLIDAY', 'NON_WORKING_DAY'];
const TYPE_CLASS: Record<DayType, string> = {
  WORKDAY: '', WEEKEND: 'weekend', HOLIDAY: 'holiday', NON_WORKING_DAY: 'nonworking',
};
const EDIT_PERMISSION = 'refs.production_calendar.edit';

const iso = (y: number, m: number, d: number) =>
  `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const uaDate = (s: string) => `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}`;
/** Понеділок = 0 … неділя = 6. */
const dowIndex = (y: number, m: number, d: number) => (new Date(y, m - 1, d).getDay() + 6) % 7;
/** День тижня дати у форматі РРРР-ММ-ДД. */
const dowOf = (day: string) =>
  dowIndex(Number(day.slice(0, 4)), Number(day.slice(5, 7)), Number(day.slice(8, 10)));
const daysInMonth = (y: number, m: number) => new Date(y, m, 0).getDate();

interface DayForm {
  day_date: string;
  day_type: DayType;
  holiday_name: string;
  comment: string;
  /** Чи є в організації власний день на цю дату (тоді доступне «повернути типовий»). */
  own: boolean;
  template_type: DayType;
  /** Дата обрана кліком у сітці (а не через «додати день»). */
  fromGrid: boolean;
}

export default function ProductionCalendarPage() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [orgId, setOrgId] = useOrgFilter();
  const [year, setYear] = useState(new Date().getFullYear());
  const [form, setForm] = useState<DayForm | null>(null);
  const [error, setError] = useState<string | null>(null);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgItems = orgList.data?.items ?? [];
  // Календар завжди однієї організації: без вибору беремо першу доступну.
  const activeOrg = orgId || orgItems[0]?.id || '';

  const { availableOrgs } = usePermissionsByOrg(EDIT_PERMISSION);
  const canEdit = availableOrgs.some((o) => o.id === activeOrg);

  const cal = useQuery({
    queryKey: ['org-calendar', activeOrg, year],
    queryFn: () => orgCalendar.year(activeOrg, year),
    enabled: !!activeOrg,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['org-calendar'] });
    setError(null);
  };
  const fail = (e: unknown) => {
    const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
    setError(typeof detail === 'string' ? detail : 'Не вдалося зберегти');
  };

  const special = useMemo(() => {
    const map = new Map<string, CalendarDay>();
    for (const d of cal.data?.days ?? []) map.set(d.day_date, d);
    return map;
  }, [cal.data]);

  const setRegime = useMutation({
    mutationFn: (isWartime: boolean) => orgCalendar.setRegime(activeOrg, isWartime),
    onSuccess: refresh, onError: fail,
  });
  const saveDay = useMutation({
    mutationFn: (f: DayForm) => orgCalendar.setDay({
      organization_id: activeOrg, day_date: f.day_date, day_type: f.day_type,
      holiday_name: f.holiday_name.trim() || null, comment: f.comment.trim() || null,
    }),
    onSuccess: () => { refresh(); setForm(null); }, onError: fail,
  });
  const resetDay = useMutation({
    mutationFn: (day: string) => orgCalendar.resetDay(activeOrg, day),
    onSuccess: () => { refresh(); setForm(null); }, onError: fail,
  });
  const resetYear = useMutation({
    mutationFn: () => orgCalendar.resetYear(activeOrg, year),
    onSuccess: refresh, onError: fail,
  });

  function typeOf(day: string): DayType {
    const s = special.get(day);
    if (s) return s.day_type;
    return dowOf(day) >= 5 ? 'WEEKEND' : 'WORKDAY';
  }
  function openDay(day: string, fromGrid: boolean) {
    const s = special.get(day);
    setForm({
      day_date: day,
      day_type: s?.day_type ?? typeOf(day),
      holiday_name: s?.source === 'OWN' ? (s.holiday_name ?? '') : '',
      comment: s?.source === 'OWN' ? (s.comment ?? '') : '',
      own: s?.source === 'OWN',
      template_type: s?.template_type ?? typeOf(day),
      fromGrid,
    });
  }

  const isWartime = cal.data?.is_wartime ?? true;
  const yearWorkdays = (cal.data?.months ?? []).reduce((a, m) => a + m.workdays, 0);
  const rows = cal.data?.days.filter((d) => d.day_type !== 'WEEKEND' || d.source === 'OWN'
    || !!d.holiday_name) ?? [];

  const cols = useMemo<ColumnDef<CalendarDay>[]>(() => [
    { key: 'day_date', header: 'Дата', width: 120,
      sortValue: (r) => r.day_date, cell: (r) => uaDate(r.day_date) },
    { key: 'dow', header: 'День', width: 70,
      cell: (r) => DOW[dowOf(r.day_date)] },
    { key: 'day_type', header: 'Тип', width: 130,
      sortValue: (r) => DAY_TYPE_LABELS[r.day_type], cell: (r) => DAY_TYPE_LABELS[r.day_type] },
    { key: 'holiday_name', header: 'Назва',
      sortValue: (r) => r.holiday_name ?? '', cell: (r) => r.holiday_name ?? '' },
    { key: 'source', header: 'Звідки', width: 150,
      sortValue: (r) => r.source,
      cell: (r) => r.source === 'OWN'
        ? <span style={{ color: 'var(--primary)', fontWeight: 600 }}>власний день</span>
        : 'типовий календар' },
    { key: 'actions', header: '', width: 170, align: 'right',
      cell: (r) => canEdit && r.source === 'OWN' ? (
        <button onClick={() => resetDay.mutate(r.day_date)} disabled={resetDay.isPending}
          title={`За типовим календарем цей день — ${DAY_TYPE_LABELS[r.template_type].toLowerCase()}`}>
          повернути типовий
        </button>
      ) : null },
  ], [canEdit, resetDay]);

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Виробничий календар</h2>
        {canEdit && (
          <button className="primary"
            onClick={() => openDay(iso(year, new Date().getMonth() + 1, 1), false)}>
            додати день
          </button>
        )}
      </div>
      <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', margin: '0 0 0.6rem' }}>
        <select value={activeOrg} onChange={(e) => setOrgId(e.target.value)} style={{ maxWidth: 280 }}
          aria-label="Організація">
          {orgItems.map((o) => <option key={o.id} value={o.id}>{withCode(o.name, o.code)}</option>)}
        </select>
        <button onClick={() => setYear((y) => y - 1)} title="Попередній рік">‹</button>
        <strong style={{ minWidth: '3.2rem', textAlign: 'center' }}>{year}</strong>
        <button onClick={() => setYear((y) => y + 1)} title="Наступний рік">›</button>
        <div className="seg-tabs" style={{ marginLeft: '0.5rem' }}>
          <button type="button" className={isWartime ? 'active' : ''}
            disabled={!canEdit || setRegime.isPending}
            onClick={() => !isWartime && setRegime.mutate(true)}>воєнний стан</button>
          <button type="button" className={!isWartime ? 'active' : ''}
            disabled={!canEdit || setRegime.isPending}
            onClick={() => isWartime && setRegime.mutate(false)}>мирний час</button>
        </div>
        <span style={{ marginLeft: 'auto', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          робочих днів у році: <strong>{yearWorkdays}</strong> · годин: <strong>{yearWorkdays * 8}</strong>
        </span>
      </div>

      <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', maxWidth: '62rem', margin: '0 0 0.6rem' }}>
        {isWartime
          ? 'Режим воєнного стану: святкових днів немає, вихідні — субота й неділя (Закон №2136-IX, ст. 6). Якщо організація дає вихідний у святковий день — позначте його як власний день.'
          : 'Режим мирного часу: святкові й неробочі дні за ст. 73 КЗпП — вихідні; якщо свято припало на суботу чи неділю, вихідний переноситься на наступний день.'}
        {' '}Календар діє лише для цієї організації.
        {!canEdit && ' У вас є право лише на перегляд.'}
      </p>

      {(error || cal.isError) && (
        <div style={{ background: 'var(--badge-red-bg)', color: 'var(--badge-red-fg)', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>
          {error ?? 'Не вдалося завантажити календар цієї організації. Перевірте, чи є у вас доступ до неї.'}
        </div>
      )}

      <div className="pcal-legend">
        <span><span className="pcal-day">1</span>робочий</span>
        <span><span className="pcal-day weekend">1</span>вихідний</span>
        <span><span className="pcal-day holiday">1</span>святковий</span>
        <span><span className="pcal-day nonworking">1</span>неробочий</span>
        <span><span className="pcal-day own">1</span>власний день організації</span>
      </div>

      <div className="pcal-year">
        {MONTHS.map((name, idx) => {
          const m = idx + 1;
          const norm = cal.data?.months.find((x) => x.month === m);
          const cells: (number | null)[] = [
            ...Array(dowIndex(year, m, 1)).fill(null),
            ...Array.from({ length: daysInMonth(year, m) }, (_, i) => i + 1),
          ];
          return (
            <div className="pcal-month" key={m}>
              <h4>{name}</h4>
              <div className="pcal-grid">
                {DOW.map((d) => <div className="pcal-dow" key={d}>{d}</div>)}
                {cells.map((d, i) => {
                  if (d === null) return <div key={`e${i}`} />;
                  const day = iso(year, m, d);
                  const s = special.get(day);
                  const t = typeOf(day);
                  const title = [
                    `${uaDate(day)} — ${DAY_TYPE_LABELS[t].toLowerCase()}`,
                    s?.holiday_name, s?.source === 'OWN' ? 'власний день організації' : '',
                  ].filter(Boolean).join(' · ');
                  return (
                    <button key={day} type="button" title={title} disabled={!canEdit}
                      className={`pcal-day ${TYPE_CLASS[t]} ${s?.source === 'OWN' ? 'own' : ''}`}
                      onClick={() => openDay(day, true)}>
                      {d}
                    </button>
                  );
                })}
              </div>
              <div className="pcal-norm">
                {norm ? `робочих днів: ${norm.workdays} · годин: ${norm.hours}` : ' '}
              </div>
            </div>
          );
        })}
      </div>

      <div className="toolbar" style={{ marginTop: '1rem' }}>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>
          Святкові, неробочі та власні дні {year} року
        </h3>
        {canEdit && (cal.data?.own_days_count ?? 0) > 0 && (
          <button style={{ marginLeft: 'auto' }} disabled={resetYear.isPending}
            onClick={() => confirm(
              `Прибрати всі власні дні ${year} року (${cal.data?.own_days_count})? `
              + 'Календар цього року стане типовим.',
            ).then((ok) => { if (ok) resetYear.mutate(); })}>
            повернути типовий рік
          </button>
        )}
      </div>
      <DataTable<CalendarDay>
        rows={rows}
        columns={cols}
        rowKey={(r) => r.day_date}
        loading={cal.isLoading}
        emptyText={isWartime
          ? 'Святкових і власних днів немає: усі будні — робочі.'
          : 'Особливих днів немає.'}
        initialSortKey="day_date"
      />

      {form && (
        <Drawer
          open
          title={form.fromGrid ? `День ${uaDate(form.day_date)}` : 'Власний день організації'}
          onClose={() => setForm(null)}
          footer={
            <>
              <button onClick={() => setForm(null)}>скасувати</button>
              {form.own && (
                <button disabled={resetDay.isPending}
                  onClick={() => resetDay.mutate(form.day_date)}>
                  повернути типовий
                </button>
              )}
              <button className="primary" disabled={saveDay.isPending}
                onClick={() => saveDay.mutate(form)}>
                зберегти
              </button>
            </>
          }
        >
          <div className="field">
            <label>Дата</label>
            <DatePopover value={form.day_date} disabled={form.fromGrid}
              onChange={(v) => {
                setYear(Number(v.slice(0, 4)));
                const s = special.get(v);
                setForm({ ...form, day_date: v, own: s?.source === 'OWN',
                  template_type: s?.template_type ?? typeOf(v) });
              }} />
          </div>
          <div className="field">
            <label>Тип дня</label>
            <select value={form.day_type}
              onChange={(e) => setForm({ ...form, day_type: e.target.value as DayType })}>
              {TYPE_ORDER.map((t) => <option key={t} value={t}>{DAY_TYPE_LABELS[t]}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Назва (необовʼязково)</label>
            <input value={form.holiday_name} maxLength={255}
              placeholder="наприклад: День заснування підприємства"
              onChange={(e) => setForm({ ...form, holiday_name: e.target.value })} />
          </div>
          <div className="field">
            <label>Коментар (необовʼязково)</label>
            <input value={form.comment} maxLength={1000}
              placeholder="підстава: наказ, колективний договір"
              onChange={(e) => setForm({ ...form, comment: e.target.value })} />
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
            За типовим календарем цей день — {DAY_TYPE_LABELS[form.template_type].toLowerCase()}.
            {' '}«Вихідний» зменшує норму робочих днів. «Святковий» і «неробочий» — теж,
            а ще такі дні не входять у тривалість щорічної відпустки, не оплачуються у
            відпустці й виключаються з розрахункового періоду відпускних.
          </p>
        </Drawer>
      )}
    </div>
  );
}
